import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { useTabs, titleForPath } from "../lib/tabs";

// Browser-style in-app tabs: every internal route you visit becomes a tab so you can
// keep several LRs / records open and switch between them without losing your place.
export function TabBar() {
  const ctx = useTabs();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    if (ctx?.openTab) ctx.openTab(pathname, titleForPath(pathname));
  }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!ctx) return null;
  const { tabs, closeTab } = ctx;
  if (tabs.length <= 1) return null; // stay out of the way until there are multiple tabs

  const onClose = (e, path) => {
    e.stopPropagation();
    const idx = tabs.findIndex((t) => t.path === path);
    const rest = tabs.filter((t) => t.path !== path);
    closeTab(path);
    if (path === pathname) {
      const next = rest[Math.max(0, idx - 1)];
      navigate(next ? next.path : "/app");
    }
  };

  return (
    <div className="flex items-stretch gap-1 overflow-x-auto bg-[hsl(var(--sidebar))] border-b border-[hsl(var(--border))] px-2 no-scrollbar"
         data-testid="app-tab-bar">
      {tabs.map((t) => {
        const active = t.path === pathname;
        return (
          <button
            key={t.path}
            onClick={() => navigate(t.path)}
            title={t.title}
            data-testid={`tab-${t.path}`}
            className={`group flex items-center gap-2 max-w-[200px] shrink-0 px-3 py-2 text-xs border-t-2 transition-colors ${
              active
                ? "bg-[hsl(var(--surface))] border-[hsl(var(--brand))] text-[hsl(var(--text-main))] font-medium"
                : "bg-transparent border-transparent text-[hsl(var(--text-muted))] hover:bg-[hsl(var(--border))]/50"
            }`}
          >
            <span className="truncate">{t.title}</span>
            <span
              onClick={(e) => onClose(e, t.path)}
              data-testid={`tab-close-${t.path}`}
              className="shrink-0 rounded-full p-0.5 opacity-50 group-hover:opacity-100 hover:bg-[hsl(var(--border))] hover:text-[#8A2526]"
            >
              <X className="w-3 h-3" />
            </span>
          </button>
        );
      })}
    </div>
  );
}
